/* global */
import React from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { Label } from 'panamera-react-ui';
import ImageGallery from 'Components/ImageGallery/ImageGallery';
import css from './InspectionTabContent.APP_TARGET.scss';
import { FLOW_STEP } from 'Constants/tracking';
import classNames from 'classnames';
import irCss from './InspectionReport.APP_TARGET.scss';
import { INSPECTION_TAB, INSPECTION_TAP_FLOW_TYPE } from 'Constants/items';
import { INSPECTION_FULL_SCREEN_HASH } from 'Constants/items';
import withRouter from 'HOCs/withRouter';
import CarMapAsync from '../../components/CarMap/CarMapAsync';
import { isMobile } from 'Helpers/devices';
import withConfig from 'HOCs/withConfig/withConfig';
import DentMapAsync from '../../components/DentMap/DentMapAsync';
import { INSPECTION_IMG_GALLERY_HEIGHT_ADPV, INSPECTION_IMG_GALLERY_WIDTH_ADPV } from 'Constants/images';

const EXTRA_SCROLL_PADDING = 12; // equal to the margins for tagContainer

export class InspectionTabContent extends React.PureComponent {
    static propTypes = {
        tags: PropTypes.array.isRequired,
        tabTitle: PropTypes.string.isRequired,
        onTrackTagTap: PropTypes.func.isRequired,
        trackImagePagination: PropTypes.func,
        onHandleImgTap: PropTypes.func.isRequired,
        selectedTab: PropTypes.string.isRequired,
        config: PropTypes.shape({
            get: PropTypes.func.isRequired
        }).isRequired
    }

    constructor(props) {
        super(props);
        this.state = {
            selectedTag: props.tags[0],
            currImgIdx: 0
        };

        this.tagContainerRef = React.createRef();
    }

    alignChips = element => {
        if (!element.parentElement.classList.contains(css.tagsContainer)) {
            element = element.parentElement;
        }

        const elemRec = element.getBoundingClientRect();
        const rightPosElm = elemRec.right;
        const leftPosElm = elemRec.left;

        const containerRec = this.tagContainerRef.current;

        const rightWidthOutOfViewPort = rightPosElm - (containerRec.offsetLeft + containerRec.offsetWidth);
        const leftWidthOutOfViewPort = leftPosElm - EXTRA_SCROLL_PADDING - containerRec.offsetLeft;

        if (rightWidthOutOfViewPort > 0) {
            const shiftLength = rightWidthOutOfViewPort + EXTRA_SCROLL_PADDING;

            this.tagContainerRef.current.scrollBy({
                left: shiftLength,
                behavior: 'smooth'
            });
        }
        else if (leftWidthOutOfViewPort < 0) {
            const shiftLength = leftWidthOutOfViewPort;

            this.tagContainerRef.current.scrollBy({
                left: shiftLength,
                behavior: 'smooth'
            });
        }
    }

    handleMapTap = tagId => {
        const tag = this.props.tags.find(tag => tag.id === tagId);

        if (!tag) {
            return;
        }
        const elem = document.querySelector(`[data-aut-id='${tagId}']`);

        this.alignChips(elem);
        this.props.onTrackTagTap({
            chosen_option: tagId,
            flow_type: INSPECTION_TAP_FLOW_TYPE.CAR_MAP
        });
        this.setState({
            selectedTag: tag,
            currImgIdx: 0
        });
    }

    handleDentMapTap = (idx, tagId) => {
        this.props.onTrackTagTap({
            chosen_option: tagId,
            flow_type: INSPECTION_TAP_FLOW_TYPE.DENT_MAP
        });

        this.handleSetCurrIdx(idx);
    }

    handleTagTap = tag => ev => {
        this.alignChips(ev.target);

        this.props.onTrackTagTap({
            chosen_option: tag.id,
            flow_type: INSPECTION_TAP_FLOW_TYPE.CHIP
        });

        this.setState({
            selectedTag: tag,
            currImgIdx: 0
        });
    }

    trackImagePagination = (trackingInfo, url, isFullScreen) => {
        this.props.trackImagePagination({
            ...trackingInfo,
            select_from: this.props.tabTitle,
            chosen_option: this.state.selectedTag.name,
            flow_step: FLOW_STEP.ADP
        }, url, isFullScreen);
    }

    handleImgTap = (trackingInfo = {}, maximize = false) => {
        const updatedTrackingInfo = {
            ...trackingInfo,
            select_from: this.props.tabTitle,
            chosen_option: this.state.selectedTag.name,
            flow_step: FLOW_STEP.ADP
        };

        this.props.onHandleImgTap(updatedTrackingInfo, maximize);
    }

    handleSetCurrIdx = idx => {
        this.setState({
            currImgIdx: idx
        });
    }

    renderTags = () => {
        const { tags } = this.props;

        return (
            <div
                className={ css.tagsContainer }
                ref={ this.tagContainerRef }>
                {
                    tags.map(tag => (
                        <Label
                            dataAutId={ tag.id }
                            key={ tag.id }
                            content={ tag.name }
                            onLabelClick={ this.handleTagTap(tag) }
                            showIcon={ false }
                            labelClass={ classNames(irCss.noOutline,
                                css.labelClass,
                                {
                                    [css.selectedLabel]: tag.id === this.state.selectedTag.id
                                }) }
                        />
                    ))
                }
            </div>
        );
    }

    render() {
        const { selectedTab, config } = this.props;
        const { selectedTag: { id, images }, currImgIdx } = this.state;
        const desc = images[currImgIdx].description;
        const isDentMapVariant = config.get('showDentMap');

        const showDentMap = isDentMapVariant && selectedTab === INSPECTION_TAB.DENT_MAP;
        const showCarMap = !isDentMapVariant && selectedTab === INSPECTION_TAB.EXTERIOR;
        const disableImgOptimization = !!this.props.config.get('disableImgOptimization');

        return (
            <React.Fragment>
                {
                    !isMobile
                    && !showDentMap
                    && this.renderTags()
                }
                <div className={ css.irPartitionContainer }>
                    {showDentMap
                        ? <div className={ css.irPartition }>
                            <div className={ css.carMap }>
                                <DentMapAsync
                                    images={ this.props.tags[0].images }
                                    currImgIdx={ currImgIdx }
                                    onDentMapTap={ this.handleDentMapTap }
                                />
                            </div>
                        </div>
                        : (showCarMap
                            && <div className={ css.irPartition }>
                                <div className={ css.carMap }>
                                    <CarMapAsync
                                        onMapTap={ this.handleMapTap }
                                        selectedTag={ id }
                                    />
                                </div>
                            </div>
                        )
                    }
                    <div className={ css.irPartition }>
                        {
                            isMobile
                            && !showDentMap
                            && this.renderTags()
                        }
                        <div className={ css.imgGalleryContainer }>
                            <ImageGallery
                                key={ this.state.selectedTag.id }
                                className={ classNames(css.galleryWrapper, {
                                    [css.dentMapGallery]: showDentMap,
                                    [css.noDesc]: !desc
                                }) }
                                images={ images }
                                imageSize={ INSPECTION_IMG_GALLERY_WIDTH_ADPV }
                                imageHeight={ INSPECTION_IMG_GALLERY_HEIGHT_ADPV }
                                setCurrIdx={ this.handleSetCurrIdx }
                                trackImagePagination={ this.trackImagePagination }
                                trackImageTap={ this.handleImgTap }
                                fullScreenHash={ INSPECTION_FULL_SCREEN_HASH }
                                customSliderCss={ css.customSliderCss }
                                showThumbsContainer={ false }
                                selectedImgIdx={ currImgIdx }
                                showLabel
                                showSliderArrows
                                useFullScreenGalleryV2
                                disableImgOptimization={ disableImgOptimization }
                            />
                            {
                                desc
                                && <div
                                    className={ css.descContainer }
                                >
                                    {images[currImgIdx].description}
                                </div>
                            }
                        </div>

                    </div>
                </div>
            </React.Fragment>
        );
    }
}

export default compose(
    withRouter,
    withConfig
)(InspectionTabContent);
